// FT-IMPORT Story 2 — optional "import a GitHub issue" section for the Fast
// Track wizard. Mounts between the Label <select> and the action buttons. When
// collapsed (the default) the wizard's manual flow is unchanged; expanding it
// lets the user pick a connected-account repo (or type owner/repo), browse the
// repo's OPEN issues, and pre-fill the Fast Track fields from a selected issue.
//
// Consumes S1's read endpoints (PRs already filtered server-side):
//   GET /github/repos
//   GET /github/repos/:owner/:repo/issues?state=open
// On selection it calls onApply(mappedFields, { owner, repo, number }) so the
// parent populates its (still-editable) form fields + carries githubSource into
// the create POST. Soft heuristics (multi-scope, thin-issue) nudge, never block.

import { useState, useRef } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import {
  mapGithubIssueToFastTrack,
  detectMultiScope,
  isThinIssue,
  FAST_TRACK_LABELS,
  type GithubRepoSummary,
  type GithubIssueSummary,
  type MappedFastTrackFields,
} from '../lib/githubIssueImport';

export interface GithubSource { owner: string; repo: string; number: number }

interface Props {
  onApply: (fields: MappedFastTrackFields, source: GithubSource) => void;
  onClear: () => void;
}

const FILTERS = ['all', ...FAST_TRACK_LABELS] as const;
type Filter = (typeof FILTERS)[number];

function parseOwnerRepo(raw: string): { owner: string; repo: string } | null {
  const m = raw.trim().match(/^([^/\s]+)\/([^/\s]+)$/);
  return m ? { owner: m[1], repo: m[2] } : null;
}

export default function GithubIssueImportSection({ onApply, onClear }: Props) {
  const [expanded, setExpanded] = useState(false);

  const [repos, setRepos]             = useState<GithubRepoSummary[] | null>(null);
  const [reposError, setReposError]   = useState('');
  const [reposLoading, setReposLoading] = useState(false);

  const [freeText, setFreeText]       = useState('');
  const [active, setActive]           = useState<{ owner: string; repo: string } | null>(null);

  const [issues, setIssues]           = useState<GithubIssueSummary[] | null>(null);
  const [issuesError, setIssuesError] = useState('');
  const [issuesLoading, setIssuesLoading] = useState(false);

  const [filter, setFilter]           = useState<Filter>('all');
  const [search, setSearch]           = useState('');
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);

  // Monotonic request token: a fast repo switch (A then B) must not let A's
  // late response overwrite B's list/selection. Each loadIssues call claims the
  // next token; only the latest-claimed call is allowed to commit its result.
  const issuesReq = useRef(0);

  async function loadRepos() {
    setReposLoading(true);
    setReposError('');
    try {
      const data = await apiFetch<GithubRepoSummary[]>('/github/repos');
      setRepos(data);
    } catch (err) {
      const code = err instanceof ApiError ? (err.body.code as string | undefined) : undefined;
      setReposError(
        code === 'GITHUB_NOT_CONNECTED'
          ? 'GitHub isn’t connected for your workspace. Connect it in Settings, or type an owner/repo below.'
          : (err instanceof Error ? err.message : 'Could not load your repositories.'),
      );
      // Leave repos === null (not []) so collapse → re-expand retries the load;
      // the free-text owner/repo fallback below stays usable meanwhile.
    } finally {
      setReposLoading(false);
    }
  }

  function handleExpand() {
    const next = !expanded;
    setExpanded(next);
    if (!next) {
      // Collapsing reads as "I don't want to import" — drop the provenance and
      // reset the picker's view state (selection, filter, search) so a later
      // manual submit carries no githubSource and a re-open starts clean.
      onClear();
      setSelectedNumber(null);
      setFilter('all');
      setSearch('');
    }
    if (next && repos === null && !reposLoading) void loadRepos();
  }

  async function loadIssues(owner: string, repo: string) {
    const token = ++issuesReq.current;
    setActive({ owner, repo });
    setIssues(null);
    setIssuesError('');
    setSelectedNumber(null);
    setFilter('all');
    setSearch('');
    onClear(); // changing repo drops any prior selection's provenance
    setIssuesLoading(true);
    try {
      const data = await apiFetch<GithubIssueSummary[]>(
        `/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues?state=open`,
      );
      if (token !== issuesReq.current) return; // a newer repo switch superseded this
      setIssues(data);
    } catch (err) {
      if (token !== issuesReq.current) return;
      const code = err instanceof ApiError ? (err.body.code as string | undefined) : undefined;
      setIssuesError(
        code === 'GITHUB_REPO_NOT_FOUND'
          ? `Repository ${owner}/${repo} was not found, or your token can’t see it.`
          : (err instanceof Error ? err.message : 'Could not load issues for that repository.'),
      );
    } finally {
      if (token === issuesReq.current) setIssuesLoading(false);
    }
  }

  function selectIssue(issue: GithubIssueSummary) {
    setSelectedNumber(issue.number);
    if (active) {
      onApply(mapGithubIssueToFastTrack(issue), { owner: active.owner, repo: active.repo, number: issue.number });
    }
  }

  const filtered = (issues ?? []).filter(issue => {
    const byLabel  = filter === 'all' || issue.labels.some(l => l.toLowerCase() === filter);
    const q        = search.trim().toLowerCase();
    const bySearch = !q || issue.title.toLowerCase().includes(q) || String(issue.number).includes(q);
    return byLabel && bySearch;
  });

  const selectedIssue = (issues ?? []).find(i => i.number === selectedNumber) ?? null;
  const mapped        = selectedIssue ? mapGithubIssueToFastTrack(selectedIssue) : null;
  const multiScope    = selectedIssue ? detectMultiScope(selectedIssue) : null;
  const thin          = selectedIssue ? isThinIssue(selectedIssue) : false;

  return (
    <div style={{ marginBottom: 14 }} data-testid="ft-import-section">
      <button
        type="button"
        onClick={handleExpand}
        aria-expanded={expanded}
        data-testid="ft-import-toggle"
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left',
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
          fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--primary)',
        }}
      >
        <span style={{ fontSize: 11 }}>{expanded ? '▼' : '▶'}</span>
        Import from a GitHub issue <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span>
      </button>

      {expanded && (
        <div
          data-testid="ft-import-body"
          style={{ marginTop: 10, padding: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface-alt)' }}
        >
          {/* Repo select */}
          <label htmlFor="ft-import-repo" style={labelStyle}>Repository</label>
          <select
            id="ft-import-repo"
            data-testid="ft-import-repo-select"
            disabled={reposLoading}
            value={active ? `${active.owner}/${active.repo}` : ''}
            onChange={e => { const p = parseOwnerRepo(e.target.value); if (p) void loadIssues(p.owner, p.repo); }}
            style={selectStyle}
          >
            <option value="">{reposLoading ? 'Loading repositories…' : 'Select a repository…'}</option>
            {(repos ?? []).map(r => (
              <option key={r.fullName} value={r.fullName}>{r.fullName}{r.private ? ' (private)' : ''}</option>
            ))}
          </select>

          {reposError && (
            <p data-testid="ft-import-repos-error" style={{ ...hintStyle, color: 'var(--text-secondary)' }}>{reposError}</p>
          )}

          {/* Free-text fallback for repos not in the connected-account list */}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              type="text"
              placeholder="or type owner/repo"
              value={freeText}
              data-testid="ft-import-repo-freetext"
              onChange={e => setFreeText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const p = parseOwnerRepo(freeText); if (p) void loadIssues(p.owner, p.repo); } }}
              style={{ ...selectStyle, flex: 1 }}
            />
            <button
              type="button"
              data-testid="ft-import-load"
              disabled={!parseOwnerRepo(freeText)}
              onClick={() => { const p = parseOwnerRepo(freeText); if (p) void loadIssues(p.owner, p.repo); }}
              style={{ ...smallBtnStyle, opacity: parseOwnerRepo(freeText) ? 1 : 0.5, cursor: parseOwnerRepo(freeText) ? 'pointer' : 'not-allowed' }}
            >
              Load
            </button>
          </div>

          {/* Issue browser */}
          {active && (
            <div style={{ marginTop: 14 }}>
              {issuesLoading && <p style={hintStyle} data-testid="ft-import-loading">Loading open issues…</p>}
              {issuesError && <p data-testid="ft-import-issues-error" style={{ ...hintStyle, color: 'var(--error)' }}>{issuesError}</p>}

              {issues && !issuesError && (
                <>
                  {/* Filter chips — bug + the other FAST_TRACK labels; filter offered, not imposed */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {FILTERS.map(f => (
                      <button
                        key={f}
                        type="button"
                        data-testid={`ft-import-chip-${f}`}
                        onClick={() => setFilter(f)}
                        style={{
                          fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '4px 10px',
                          borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                          border: filter === f ? '1px solid var(--primary)' : '1px solid var(--border)',
                          background: filter === f ? 'rgba(124,58,237,0.12)' : 'transparent',
                          color: filter === f ? 'var(--primary)' : 'var(--text-secondary)',
                        }}
                      >
                        {f === 'all' ? 'All' : f}
                      </button>
                    ))}
                    <input
                      type="text"
                      placeholder="Search issues…"
                      value={search}
                      data-testid="ft-import-search"
                      onChange={e => setSearch(e.target.value)}
                      style={{ ...selectStyle, flex: 1, minWidth: 120, height: 30 }}
                    />
                  </div>

                  {/* Issue list */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 240, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                    {filtered.length === 0 ? (
                      <p style={hintStyle} data-testid="ft-import-empty">
                        {issues.length === 0 ? 'No open issues in this repository.' : 'No issues match your filter.'}
                      </p>
                    ) : filtered.map(issue => {
                      const isSel = selectedNumber === issue.number;
                      return (
                        <button
                          key={issue.number}
                          type="button"
                          data-testid={`ft-import-issue-${issue.number}`}
                          onClick={() => selectIssue(issue)}
                          style={{
                            textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 3, padding: '8px 10px',
                            borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                            border: isSel ? '1px solid var(--primary)' : '1px solid var(--border)',
                            background: isSel ? 'rgba(124,58,237,0.06)' : 'var(--surface)',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', fontWeight: isSel ? 600 : 500, flex: 1 }}>
                              {issue.title}
                            </span>
                            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>#{issue.number}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {issue.labels.slice(0, 4).map(l => (
                              <span key={l} style={{ fontFamily: 'var(--font-body)', fontSize: 11, padding: '1px 6px', borderRadius: 'var(--radius-sm)', background: 'rgba(124,58,237,0.10)', color: 'var(--primary)' }}>{l}</span>
                            ))}
                            {issue.body && (
                              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                                {issue.body.replace(/\s+/g, ' ').trim().slice(0, 100)}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Selection preview — thinness visible BEFORE create */}
              {selectedIssue && mapped && (
                <div data-testid="ft-import-preview" style={{ marginTop: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                    Will populate (you can still edit)
                  </div>
                  <PreviewRow label="Title" value={mapped.title} />
                  <PreviewRow label="Description" value={mapped.description ? mapped.description.replace(/\s+/g, ' ').trim().slice(0, 120) : '— (empty)'} />
                  <PreviewRow label="Label" value={mapped.label ?? '— (kept your choice; no matching label)'} />

                  {multiScope?.isMultiScope && (
                    <div data-testid="ft-import-multiscope-warning" role="note" aria-label="Multi-scope warning" style={nudgeStyle('warning')}>
                      <strong>This looks multi-scope</strong> — {multiScope.reasons.join(', ')}. Fast Track is single-issue;
                      for work this broad, the standard New Project flow (with Phase 1/2 review) is usually a better fit.
                    </div>
                  )}
                  {thin && (
                    <div data-testid="ft-import-thin-warning" role="note" aria-label="Thin issue warning" style={nudgeStyle('info')}>
                      <strong>This issue has little detail.</strong> Sprint Planning works from the description — consider
                      adding more before creating, so there&apos;s something to plan against.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontFamily: 'var(--font-body)', fontSize: 12, marginBottom: 3 }}>
      <span style={{ color: 'var(--text-secondary)', minWidth: 78 }}>{label} &rarr;</span>
      <span style={{ color: 'var(--text)', flex: 1, wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 4 };
const hintStyle: React.CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0' };
const selectStyle: React.CSSProperties = {
  display: 'block', width: '100%', height: 34, background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', padding: '0 10px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', outline: 'none', boxSizing: 'border-box',
};
const smallBtnStyle: React.CSSProperties = {
  height: 34, padding: '0 14px', fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
  borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--primary)', color: 'white',
};
function nudgeStyle(kind: 'warning' | 'info'): React.CSSProperties {
  const c = kind === 'warning' ? '245,158,11' : '99,102,241';
  return {
    marginTop: 10, padding: '8px 10px', fontFamily: 'var(--font-body)', fontSize: 12, lineHeight: 1.5,
    color: 'var(--text)', background: `rgba(${c},0.08)`, border: `1px solid rgba(${c},0.25)`, borderRadius: 'var(--radius-sm)',
  };
}
